/**
 * Nastavení → Slovníky — the vocabularies themselves (CZNA-1470, NK `orpa/nove-autority#12`).
 *
 * <p>A methodologist who needs a new vocabulary used to find the only create button in
 * a derivation rule's detail, where it asked for a code and a BIBFRAME role. Here the
 * vocabulary administrator sees every vocabulary with whether it is open or closed and
 * how many terms it has, creates one by its name and renames one. Terms are still
 * edited under Slovníky in the rail (`/vocabularies`); "Termíny" leads there filtered.
 *
 * <p>The registry is small (tens of rows) and the endpoint returns it in one shot, so
 * the list filters client-side, like `DerivationRulesList`.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Paneset, Pane, PaneMenu, Button, MultiColumnList, SearchField, AccordionSet, Accordion, MessageBanner, Callout, Layout, Headline, } from '@folio/stripes/components';
import NavPaneLayout from '../components/NavPaneLayout';
import VocabularyTypeFormModal from '../components/VocabularyTypeAdmin/VocabularyTypeFormModal';
import { useVocabularyTypes } from '../hooks/useVocabularyTypes';
import { vocabularyTypeLabel } from '../hooks/useVocabularyTypeOptions';
import { TableLoadingSkeleton } from '../components/TableStates';
var VOCABULARIES_PATH = '/czna-authorities/vocabularies';
export var COLUMN_WIDTHS = {
    name: '40%',
    validationType: '16%',
    termCount: '12%',
    actions: '32%',
};
var VISIBLE_COLUMNS = ['name', 'validationType', 'termCount', 'actions'];
var VocabularyTypesList = function () {
    var intl = useIntl();
    var history = useHistory();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var calloutRef = React.useRef(null);
    var _a = useState(''), query = _a[0], setQuery = _a[1];
    var _b = useState(true), filterPaneVisible = _b[0], setFilterPaneVisible = _b[1];
    var _c = useState({ modal: null }), modalState = _c[0], setModalState = _c[1];
    var _d = useVocabularyTypes({ includeTermCounts: true }), vocabularyTypes = _d.vocabularyTypes, isLoading = _d.isLoading, isError = _d.isError;
    var nameOf = useCallback(function (t) { return vocabularyTypeLabel(t, intl.locale); }, [intl.locale]);
    var rows = useMemo(function () {
        var q = query.trim().toLowerCase();
        return vocabularyTypes
            .filter(function (t) {
            var _a;
            return !q
                || t.labelCs.toLowerCase().includes(q)
                || ((_a = t.labelEn) !== null && _a !== void 0 ? _a : '').toLowerCase().includes(q);
        })
            .sort(function (a, b) { return nameOf(a).localeCompare(nameOf(b), 'cs'); });
    }, [vocabularyTypes, query, nameOf]);
    var openTerms = useCallback(function (t) {
        history.push("".concat(VOCABULARIES_PATH, "?type=").concat(encodeURIComponent(t.code)));
    }, [history]);
    var handleSaved = useCallback(function (saved, mode) {
        var _a;
        setModalState({ modal: null });
        (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
            type: 'success',
            message: intl.formatMessage({ id: "ui-czna-authorities.vocabularyTypes.".concat(mode, ".success") }, { name: nameOf(saved) }),
        });
    }, [intl, nameOf]);
    var columnMapping = useMemo(function () { return ({
        name: React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.column.name" }),
        validationType: React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.column.validationType" }),
        termCount: React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.column.termCount" }),
        actions: React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.column.actions" }),
    }); }, []);
    var formatter = useMemo(function () { return ({
        name: function (t) { return React.createElement("span", { "data-testid": "vocabulary-type-name-".concat(t.code) }, nameOf(t)); },
        validationType: function (t) { return intl.formatMessage({ id: "ui-czna-authorities.vocabularyTypes.validationType.".concat(t.validationType) }); },
        termCount: function (t) { return (t.termCount == null ? '—' : intl.formatNumber(t.termCount)); },
        actions: function (t) { return (React.createElement("span", { style: { display: 'inline-flex', gap: '8px', alignItems: 'center' } },
            React.createElement(Button, { marginBottom0: true, onClick: function (e) { e.stopPropagation(); openTerms(t); }, "data-testid": "vocabulary-type-terms-".concat(t.code) },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.action.terms" })),
            t.readOnly ? (React.createElement("span", { style: { color: '#666' } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.readOnly" }))) : (React.createElement(Button, { marginBottom0: true, onClick: function (e) { e.stopPropagation(); setModalState({ modal: 'rename', type: t }); }, "data-testid": "vocabulary-type-rename-".concat(t.code) },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.action.rename" }))))); },
    }); }, [intl, nameOf, openTerms]);
    var renderContent = function () {
        if (isLoading) {
            return React.createElement(TableLoadingSkeleton, { testId: "vocabulary-types-loading", columns: [6, 3, 2, 4], rows: 8 });
        }
        if (isError) {
            return (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.list.loadError" })));
        }
        if (rows.length === 0) {
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement(Headline, { size: "medium", margin: "none" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.list.empty" })))));
        }
        return (
        /* @ts-expect-error - stripes MCL types require all entity keys but only visible columns are needed */
        React.createElement(MultiColumnList, { id: "vocabulary-types-list", contentData: rows, columnMapping: columnMapping, columnWidths: COLUMN_WIDTHS, visibleColumns: VISIBLE_COLUMNS, formatter: formatter, nonInteractiveHeaders: VISIBLE_COLUMNS }));
    };
    var lastMenu = (React.createElement(PaneMenu, null,
        React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: function () { return setModalState({ modal: 'create' }); }, "data-testid": "vocabulary-type-create" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.action.create" }))));
    return (React.createElement(React.Fragment, null,
        React.createElement(Paneset, null,
            filterPaneVisible && (React.createElement(Pane, { id: "vocabulary-types-filter-pane", defaultWidth: "22%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.pane.navigation" }), dismissible: true, onClose: function () { return setFilterPaneVisible(false); } },
                React.createElement(NavPaneLayout, null,
                    React.createElement("hr", { style: { border: 'none', borderTop: '1px solid #d9d9d9', margin: '12px 0' } }),
                    React.createElement(AccordionSet, null,
                        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.search' }), separator: false },
                            React.createElement(SearchField, { name: "query", value: query, onChange: function (e) { return setQuery(e.target.value); }, onClear: function () { return setQuery(''); }, placeholder: intl.formatMessage({ id: 'ui-czna-authorities.vocabularyTypes.search.placeholder' }) })))))),
            React.createElement(Pane, { id: "vocabulary-types-content-pane", defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.title" }), paneSub: intl.formatMessage({ id: 'ui-czna-authorities.vocabularyTypes.resultCount' }, { count: rows.length }), lastMenu: lastMenu }, renderContent())),
        React.createElement(Callout, { ref: calloutRef }),
        React.createElement(VocabularyTypeFormModal, { open: modalState.modal !== null, type: modalState.modal === 'rename' ? modalState.type : null, onClose: function () { return setModalState({ modal: null }); }, onSaved: handleSaved })));
};
export default VocabularyTypesList;
